<template>
  <div class="user-home">
    <!-- Stats Grid -->
    <div class="stats-grid">
      <!-- Card 1: 工人总数 -->
      <div class="stat-card theme-blue">
        <span class="stat-label">工人总数</span>
        <span class="stat-value">{{ stats['工人总数'] }}</span>
        <div class="stat-icon icon-blue">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path xmlns="http://www.w3.org/2000/svg" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
        </div>
      </div>

      <!-- Card 2: 在职人数 -->
      <div class="stat-card theme-green">
        <span class="stat-label">在职人数</span>
        <span class="stat-value">{{ stats['在职人数'] }}</span>
        <div class="stat-icon icon-green">
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
             <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z"/>
           </svg>
        </div>
      </div>

      <!-- Card 3: 预警人数 -->
      <div class="stat-card theme-orange">
        <span class="stat-label">预警人数</span>
        <span class="stat-value">{{ stats['预警人数'] }}</span>
        <div class="stat-icon icon-orange">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
        </div>
      </div>

      <!-- Card 4: 黑名单 -->
      <div class="stat-card theme-red">
        <span class="stat-label">黑名单</span>
        <span class="stat-value">{{ stats['黑名单'] }}</span>
        <div class="stat-icon icon-red">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/>
          </svg>
        </div>
      </div>
    </div>


    <!-- Chart Section -->
    <div class="chart-section">
      <div class="section-header">
        <h2>员工年龄分布</h2>
      </div>
      <div class="chart-container">
        <BarChart :chartData="chartData" :options="chartOptions"></BarChart>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import BarChart from '../../components/BarChart.vue';
import userHomeService from "@/api/userHomeService";
import { useStore } from "vuex";
import { ElMessage } from "element-plus";

export default {
  name: 'UserHome',
  components: {
    BarChart,
  },
  setup() {
    const store = useStore();
    const oid = computed(() => store.getters.getOid);

     // Add Date formatted
    const currentDate = computed(() => {
      const date = new Date();
      return date.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
    });

    if (!oid.value) {
      console.error('OID is not defined');
      ElMessage.error('OID 未定义');
      return;
    }

    const stats = ref({
      '工人总数': 0,
      '在职人数': 0,
      '预警人数': 0,
      '黑名单': 0,
    });

    const chartData = ref({ labels: [], datasets: [] });
    const chartOptions = ref({});

    const fetchStats = async () => {
      try {
        const response = await userHomeService.getHomeStats(oid.value);
        const data = response.data;
        stats.value['工人总数'] = data['total'];
        stats.value['在职人数'] = data['onjobNumber'];
        stats.value['预警人数'] = data['warnNumber'];
        stats.value['黑名单'] = data['blacklistNumber'];
      } catch (error) {
        console.error('Error fetching user stats:', error);
      }
    };

    const fetchAgeDistribution = async () => {
      try {
        const response = await userHomeService.getChartData(oid.value);
        const data = response.data;
        const inactiveCounts = data.totalCounts.map((total, index) => total - data.activeCounts[index]);
        const maxValue = Math.max(...data.totalCounts);

        chartData.value = {
          labels: data.labels,
          datasets: [
            {
              label: '在职',
              backgroundColor: '#3b82f6', // blue-500
              data: data.activeCounts,
              borderWidth: 0,
              barPercentage: 0.6,
              categoryPercentage: 0.8,
              borderRadius: 4
            },
            {
              label: '离职',
              backgroundColor: '#cbd5e1', // slate-300
              data: inactiveCounts,
              borderWidth: 0,
              barPercentage: 0.6,
              categoryPercentage: 0.8,
              borderRadius: 4
            }
          ]
        };

        chartOptions.value = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              align: 'center',
              labels: {
                usePointStyle: true,
                boxWidth: 8,
                padding: 15,
                font: { size: 12 }
              }
            },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                padding: 12,
                cornerRadius: 8,
                displayColors: true,
            }
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: { font: { size: 12 }, color: '#64748b' }
            },
            y: {
              stacked: true,
              beginAtZero: true,
              max: Math.ceil(maxValue * 1.2),
              grid: {
                color: '#f1f5f9',
                borderDash: [4, 4],
                drawBorder: false
              },
              ticks: {
                stepSize: Math.max(1, Math.ceil(maxValue / 5)),
                font: { size: 12 },
                color: '#64748b'
              }
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          }
        };
      } catch (error) {
        console.error('Error fetching age distribution:', error);
      }
    };

    onMounted(async () => {
      await fetchStats();
      await fetchAgeDistribution();
    });

    return {
      stats,
      chartData,
      chartOptions,
      currentDate
    };
  },
};
</script>

<style scoped>
.user-home {
  padding: 16px; /* Reduced specific padding */
  width: 100%;
  font-family: 'Public Sans', 'Noto Sans SC', sans-serif;
  color: #1e293b;
  height: calc(100vh - 50px); /* Fill remaining height */
  display: flex;
  flex-direction: column;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  flex-shrink: 0; /* Keep stats full size */
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  align-items: center; /* Vertically center all items */
  justify-content: space-between; /* Space them out */
  box-shadow: 0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06);
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: currentColor;
  opacity: 0;
  transition: opacity 0.2s;
}

.stat-card:hover::before {
  opacity: 1;
}

.stat-label {
  font-size: 1rem; /* Slightly larger for single line */
  font-weight: 500;
  color: #64748b;
  flex: 0 0 auto; /* Don't grow */
}

.stat-value {
  flex: 1; /* Take all available middle space */
  text-align: center; /* Center horizontally */
  font-size: 2.5rem; /* Large font */
  font-weight: 700;
  color: #0f172a;
  line-height: 1;
  margin: 0 16px; /* Add spacing around number */
}

/* Theme Colors for Values */
.theme-blue .stat-value { color: #3b82f6; }
.theme-green .stat-value { color: #22c55e; }
.theme-orange .stat-value { color: #f97316; }
.theme-red .stat-value { color: #ef4444; }

.stat-icon {
  width: 56px; /* Larger icon box to match large font */
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.stat-icon svg {
  width: 28px;
  height: 28px;
}

/* Icon Color Themes */
.icon-blue {
  background-color: #eff6ff;
  color: #3b82f6;
}
.stat-card:has(.icon-blue) { color: #3b82f6; } /* For top border */

.icon-green {
  background-color: #f0fdf4;
  color: #22c55e;
}
.stat-card:has(.icon-green) { color: #22c55e; }

.icon-orange {
  background-color: #fff7ed;
  color: #f97316;
}
.stat-card:has(.icon-orange) { color: #f97316; }

.icon-red {
  background-color: #fef2f2;
  color: #ef4444;
}
.stat-card:has(.icon-red) { color: #ef4444; }


/* Chart Section */
.chart-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border: 1px solid #e2e8f0;
  flex: 1; /* Take remaining space */
  display: flex;
  flex-direction: column;
  min-height: 0; 
  margin-bottom: 24px; /* Bottom spacing */
}

.section-header {
  margin-bottom: 1rem;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 1rem;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.section-header h2 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.chart-container {
  flex: 1;
  width: 100%;
  min-height: 0; /* Critical for chart resizing in flex container */
  position: relative;
}
</style>
